<template>
  <div>
    <RevForm
      ref="form"
      :initial-values="initialValues"
      method="POST"
      :validate="validators"
      @submit="handleSubmit"
    >
      <template #default="{ values, errors, hasError }">
        <h2 class="heading-3">
          {{ i18n(deliveryTitle) }}
        </h2>
        <RevCard
          class="mt-16 flex flex-col items-start gap-5 p-24 lg:flex-row lg:items-center lg:justify-between lg:gap-6"
          data-qa="shippingAddressSummary"
        >
          <Address
            key="shipping-address"
            :address="deliveryAddress"
            :with-details="!addressStore.hasCollectionPoint"
            with-phone
          />

          <RevButtonTiny
            data-qa="editShippingAddress"
            :icon="IconEdit"
            type="button"
            variant="secondary"
            @click="router.push({ name: CHECKOUT.SHIPPING_ADDRESS })"
          >
            {{ i18n(deliveryEditButton) }}
          </RevButtonTiny>
        </RevCard>

        <h2 class="heading-3 mt-56">
          {{ i18n(translations.billingAddressTitle) }}
        </h2>
        <RevCard
          class="mt-16 flex flex-col items-start gap-5 p-24 lg:flex-row lg:items-center lg:justify-between lg:gap-6"
          data-qa="billingAddressSummary"
        >
          <Address
            key="billing-address"
            :address="addressStore.billing"
            with-details
          />
          <RevButtonTiny
            data-qa="editBillingAddress"
            :icon="IconEdit"
            type="button"
            variant="secondary"
            @click="router.push({ name: CHECKOUT.BILLING_ADDRESS })"
          >
            {{ i18n(translations.billingAddressEditButton) }}
          </RevButtonTiny>
        </RevCard>

        <div v-if="userInformationStore.isFormRequired" class="mt-56">
          <h2 class="heading-3">
            {{ i18n(translations.userInformationTitle) }}
          </h2>
          <RevCard class="mt-16 p-24">
            <UserInformationFields
              v-model:birthdate="values.birthdate"
              v-model:national-id="values.nationalId"
              v-model:nationality="values.nationality"
              :country="addressStore.billing.country"
              :errors="errors"
              :with-national-id="
                withNationalId || userInformationWithNationalId
              "
              :with-nationality="userInformationStore.isNationalityRequired"
            />

            <div class="mt-56 flex justify-end">
              <RevButton
                data-qa="submit-button"
                :disabled="isSubmitButtonDisabled(hasError)"
                full-width="responsive"
                :loading="isSubmitting"
                type="submit"
                variant="primary"
              >
                {{ submitButtonTitle }}
              </RevButton>
            </div>
          </RevCard>
        </div>

        <div
          v-if="!userInformationStore.isFormRequired"
          class="mt-56 flex justify-end"
        >
          <RevButton
            ref="submit"
            data-qa="submit-button"
            :disabled="isSubmitButtonDisabled(hasError)"
            full-width="responsive"
            :loading="isSubmitting"
            type="submit"
            variant="primary"
          >
            {{ submitButtonTitle }}
          </RevButton>
        </div>
      </template>
    </RevForm>
    <ReassuranceItems class="mt-56" />
  </div>
</template>

<script lang="ts" setup>
import { useRoute, useRouter, useTheToast } from '#imports'
import { computed, onMounted, ref } from 'vue'

import type { Country, cartAPI } from '@backmarket/http-api'
import Address from '@backmarket/nuxt-module-address/Address.vue'
import { InputAddressBirthdateValidators } from '@backmarket/nuxt-module-address/InputAddressBirthdateValidators'
import { InputAddressNationalIdValidators } from '@backmarket/nuxt-module-address/InputAddressNationalIdValidators'
import { InputAddressNationalityValidators } from '@backmarket/nuxt-module-address/InputAddressNationalityValidators'
import UserInformationFields from '@backmarket/nuxt-module-address/UserInformationFields.vue'
import type { NationalIdCountries } from '@backmarket/nuxt-module-address/nationalId'
import {
  isNationalIdRequiredForCountry,
  isNationalIdShownForBilling,
} from '@backmarket/nuxt-module-address/utils'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { insertIf } from '@backmarket/utils/collection/insertIf'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { RevButton } from '@ds/components/Button'
import { RevButtonTiny } from '@ds/components/ButtonTiny'
import { RevCard } from '@ds/components/Card'
import { RevForm, makeValidate } from '@ds/components/Form'
import { IconEdit } from '@ds/icons/IconEdit'

import ReassuranceItems from '~/scopes/reassurance/components/ReassuranceItems/ReassuranceItems.vue'

import { CHECKOUT_LOG_TYPES } from '../../config'
import { CHECKOUT } from '../../routes-names'
import { useAddressStore } from '../../stores/addressStore'
import { useCartStore } from '../../stores/cartStore'
import { useUserInformationStore } from '../../stores/userInformationStore'

import translations from './AddressConfirmation.translations'

type FormValues = {
  country: string
  nationality: string
  birthdate: string
  nationalId?: string
}

const form = ref<HTMLFormElement | null>(null)

const isSubmitting = ref(false)

const i18n = useI18n()
const tracking = useTracking()
const route = useRoute()
const router = useRouter()

const { openErrorToast } = useTheToast()

const marketPlaceStore = useMarketplace()

const cartStore = useCartStore()
const addressStore = useAddressStore()
const userInformationStore = useUserInformationStore()

onMounted(async () => {
  if (!isEmpty(route.query.missingData)) {
    openErrorToast({
      content: i18n(translations.missingData),
    })

    // Trigger the form submit to display the missing fields error
    form.value?.submit()
  }
})

const validators = makeValidate<FormValues>({
  ...insertIf(userInformationStore.isFormRequired, {
    birthdate: InputAddressBirthdateValidators(i18n),
    nationality: InputAddressNationalityValidators(i18n),
    ...insertIf(
      !isEmpty(addressStore.billing.customerIdNumber),
      InputAddressNationalIdValidators(i18n),
    ),
  }),
})

const initialValues = computed((): FormValues => {
  const nationalId = addressStore.billing.customerIdNumber

  return {
    country: addressStore.billing.country || '',
    nationality:
      userInformationStore.nationality || marketPlaceStore.market.countryCode,
    birthdate: userInformationStore.birthdate || '',
    // isEmpty from lodash does not tell Typescript that the variable can no longer be null
    // More info: https://github.com/DefinitelyTyped/DefinitelyTyped/issues/45521
    ...insertIf(!isEmpty(nationalId), { nationalId } as { nationalId: string }),
  }
})

const withNationalId = computed(() => {
  return isNationalIdRequiredForCountry(
    addressStore.billing.country as NationalIdCountries,
  )
})

const userInformationWithNationalId = computed(() => {
  return isNationalIdShownForBilling(addressStore.billing.country as Country)
})

const deliveryAddress = computed(() => {
  // Since we are checking if it has collectionPoint this can't be undefined
  return addressStore.hasCollectionPoint
    ? (addressStore.collectionPoint as cartAPI.DeliverCollectionPoint)
    : addressStore.shipping
})

const deliveryTitle = computed(() => {
  return addressStore.hasCollectionPoint
    ? translations.collectionPointAddressTitle
    : translations.shippingAddressTitle
})

const deliveryEditButton = computed(() => {
  return addressStore.hasCollectionPoint
    ? translations.collectionPointAddressEditButton
    : translations.shippingAddressEditButton
})

const submitButtonTitle = computed(() => {
  return i18n(translations.submitButton)
})

const isSubmitButtonDisabled = (hasError: boolean) =>
  userInformationStore.isFormRequired && (isSubmitting.value || hasError)

const handleUserInformationSubmit = async (values: FormValues) => {
  tracking.trackFormSubmit({
    name: 'Address submit',
    address: { ...values },
    type: CHECKOUT_LOG_TYPES.ADDRESS_SUBMIT_CONFIRMATION,
  })

  isSubmitting.value = true

  try {
    const address: cartAPI.Bill = {
      ...addressStore.billing,
      ...values,
      countryDialInCode: addressStore.shipping.countryDialInCode,
      phone: addressStore.shipping.phone,
      ...insertIf(
        userInformationWithNationalId.value && !isEmpty(values.nationalId),
        {
          customerIdNumber: values.nationalId as string,
        },
      ),
      ...insertIf(withNationalId.value && !isEmpty(values.nationalId), {
        customerIdNumber: values.nationalId as string,
      }),
    }

    const formType = userInformationStore.isFormRequired
      ? userInformationStore.formType
      : undefined

    await addressStore.saveAddress({
      address,
      formType,
      isShipping: false,
      isBilling: true,
    })

    await cartStore.fetchCart()

    return true
  } catch {
    // Should we potentially track something?
    return false
  } finally {
    isSubmitting.value = false
  }
}

const handleSubmit = async (values: FormValues) => {
  tracking.trackClick({
    zone: 'cart',
    name: '3_shpping-confirm_cta_continue',
  })

  if (userInformationStore.isFormRequired) {
    const isSuccess = await handleUserInformationSubmit(values)

    if (!isSuccess) {
      openErrorToast({
        title: i18n(translations.submitSwapInfoTitle),
        content: i18n(translations.submitSwapInfoError),
      })
    }
  }

  router.push({ name: CHECKOUT.PAYMENT })
}
</script>
